import { z } from 'zod';

export const ROLES = ['owner', 'host', 'runner', 'waitress', 'cleaner', 'viewer'] as const;

export const roleSchema = z.enum(ROLES);

export type Role = z.infer<typeof roleSchema>;

export const MEMBERSHIP_STATUSES = ['active', 'invited', 'inactive'] as const;

export const membershipStatusSchema = z.enum(MEMBERSHIP_STATUSES);

export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const CAPABILITIES = [
  'venue:read',
  'spot:status',
  'reservation:write',
  'reservation:activity',
  'venue:settings',
  'venue:layout',
  'venue:team',
] as const;

export const capabilitySchema = z.enum(CAPABILITIES);

export type Capability = z.infer<typeof capabilitySchema>;

export const membershipSchema = z.object({ role: roleSchema, status: membershipStatusSchema });

export type Membership = z.infer<typeof membershipSchema>;

// A viewer watches the floor and changes nothing on it: every read the app
// makes of a venue is this one capability, and it is all they hold. See §4.
const VIEWER_CAPABILITIES: readonly Capability[] = ['venue:read'];

// v1 keeps staff permissions broad; only owners configure the venue. See §4.
const STAFF_CAPABILITIES: readonly Capability[] = [
  ...VIEWER_CAPABILITIES,
  'spot:status',
  'reservation:write',
];

// The restore screen reads a venue's whole history, so it stops at hosts.
const HOST_CAPABILITIES: readonly Capability[] = [...STAFF_CAPABILITIES, 'reservation:activity'];

// What each role holds, as data: a new role is an entry here and a label below.
const HELD: Readonly<Record<Role, readonly Capability[]>> = {
  owner: CAPABILITIES,
  host: HOST_CAPABILITIES,
  runner: STAFF_CAPABILITIES,
  waitress: STAFF_CAPABILITIES,
  cleaner: STAFF_CAPABILITIES,
  viewer: VIEWER_CAPABILITIES,
};

// The platform operator holds owner rights in every venue, row or no row. See §3.
export const PLATFORM_MEMBERSHIP: Membership = { role: 'owner', status: 'active' };

// Whether those owner rights are already the caller's own, so no badge is due.
export function isActiveOwner(membership: Membership | null): boolean {
  return membership !== null && membership.role === 'owner' && membership.status === 'active';
}

// Whole membership, not a bare role: invited/inactive lose access. See §4.
export function can(membership: Membership, capability: Capability): boolean {
  if (membership.status !== 'active') return false;
  return HELD[membership.role].includes(capability);
}

// A role that only watches: the app offers it no control it cannot use. §4
export function readOnly(role: Role): boolean {
  return HELD[role].every((capability) => capability === 'venue:read');
}

export const LANGUAGES = ['el', 'en'] as const;

export const languageSchema = z.enum(LANGUAGES);

export type Language = z.infer<typeof languageSchema>;

// One source for role copy: the invite email and the team screen read the same map.
const ROLE_LABELS: Readonly<Record<Language, Readonly<Record<Role, string>>>> = {
  el: {
    owner: 'Ιδιοκτήτης',
    host: 'Υποδοχή',
    runner: 'Ράνερ',
    waitress: 'Σερβιτόρος/α',
    cleaner: 'Καθαριότητα',
    viewer: 'Παρατηρητής',
  },
  en: {
    owner: 'Owner',
    host: 'Host',
    runner: 'Runner',
    waitress: 'Waitress',
    cleaner: 'Cleaner',
    viewer: 'Viewer',
  },
};

export function roleLabel(role: Role, language: Language): string {
  return ROLE_LABELS[language][role];
}
